import React, { useCallback, useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import styled from 'styled-components';
import * as API from 'api';
import { IApiQueryOptions } from 'api/ApiClient';
import {
  ButtonExportCSVAsync,
  Empty,
  EmptyTable,
  FieldWrapper,
  Filter,
  FilterButton,
  HeaderCell,
  HeaderRow,
  Input,
  ModalFactory,
  PaginatedList,
  PaymentOverviewStats,
  PaymentStatusPills,
  Section,
  Select,
  SortLabel,
  Table,
  TableBody,
  TableHead,
} from 'components';
import { DDOption } from 'config/const';
import { theme, toRem } from 'design';
import { useQueryString } from 'hooks';
import { getQueryStringValue } from 'hooks/useQueryString';
import { PayoutDetails } from 'modals';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getSitePayoutsRequest, Payouts, selectPayouts, selectTotalCountPayouts } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { getSitePayoutStatsRequest, selectPayoutStats, Stats } from 'store/Stats';
import { IStore } from 'store/types';
import { ApiQueryOperator, IApiQueryParam, IPayout, PayoutApprovalStatus, PayoutStatus } from 'types';
import { CSVFormatter, IExportCSV } from 'utils/csvformatter';
import { formatDateForApi } from 'utils/date';
import { capitalize } from 'utils/strings';
import BusinessDaySection from './BusinessDaySection';
import { DetailsContentHeader, TransactionRow } from './styles';

const FilterContainer = styled.div`
  position: relative;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  padding: 0 0 0 ${toRem(theme.layout.gutter.small)};
  z-index: 1;

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: 0 0 0 ${toRem(theme.layout.gutter.large)};
  }

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;

    > div {
      margin-bottom: 0;
      padding-bottom: 0;
      border-bottom: none;
    }
  }
`;

const FilterWrapper = styled.div`
  position: relative;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  padding: 0 ${toRem(theme.layout.gutter.small)} 0 0;
  border-bottom: 0;

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: 0 ${toRem(theme.layout.gutter.large)} 0 0;
  }
`;

const DEFAULT_START_DATE = formatDateForApi(dayjs(new Date()).subtract(6, 'day').format());
const DEFAULT_END_DATE = formatDateForApi(dayjs(new Date()).format());

export const DetailsTab = () => {
  const dispatch = useDispatch();
  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [currentModalPayout, setCurrentModalPayout] = useState<IPayout | null>(null);
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.GET_SITE_PAYOUTS_REQUEST));
  const isStatsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Stats.GET_SITE_PAYOUT_STATS_REQUEST));
  const payouts = useSelector(selectPayouts());
  const totalPayouts = useSelector(selectTotalCountPayouts());
  const payoutStats = useSelector(selectPayoutStats());

  const [urlQuery, setUrlQuery] = useQueryString('payoutDetails', window.location, {
    q: [
      { key: 'payment_date', operator: ApiQueryOperator.gte, value: DEFAULT_START_DATE },
      { key: 'payment_date', operator: ApiQueryOperator.lte, value: DEFAULT_END_DATE },
    ],
    sort: '-payment_date',
    limit: 10,
  });

  const onDateRangeChange = (startDate: string, endDate: string) => {
    const query = (urlQuery.q || []).filter((item: IApiQueryParam) => item.key !== 'payment_date');
    query.push({ key: 'payment_date', operator: ApiQueryOperator.gte, value: startDate });
    query.push({ key: 'payment_date', operator: ApiQueryOperator.lte, value: endDate });
    onQueryChange({ ...urlQuery, q: query, offset: 0 });
  };

  const onFilterChange = (value: string | DDOption[], key: string) => {
    const otherFilters = (urlQuery.q || []).filter((query: IApiQueryParam) => query.key !== key);
    const isString = typeof value === 'string';
    const newValue = isString ? value.trim() : value.map(({ value }: DDOption) => value);
    const filters = newValue.length ? [...otherFilters, { key, operator: isString ? ApiQueryOperator.like : ApiQueryOperator.in, value: newValue }] : otherFilters;
    onQueryChange({ ...urlQuery, q: filters, offset: 0 });
  };
  const debouncedOnFilterChange = debounce(onFilterChange, 500);

  const onQueryChange = (options: IApiQueryOptions) => {
    setUrlQuery(options);
  };

  const isSortedBy = (headerValue: string) => urlQuery.sort === headerValue || urlQuery.sort === `-${headerValue}`;
  const isSortDesc = urlQuery.sort?.[0] === '-';
  const onSortChange = (key: string) => {
    setUrlQuery({ ...urlQuery, sort: urlQuery.sort === key ? `-${urlQuery.sort}` : key });
  };

  const startDate = getQueryStringValue(urlQuery.q, 'payment_date', ApiQueryOperator.gte) as string;
  const endDate = getQueryStringValue(urlQuery.q, 'payment_date', ApiQueryOperator.lte) as string;
  const payeeNameTextSearch = getQueryStringValue(urlQuery.q, 'employee_name', ApiQueryOperator.like);
  const paymentIdTextSearch = getQueryStringValue(urlQuery.q, 'id', ApiQueryOperator.like);
  const status = getQueryStringValue(urlQuery.q, 'status', ApiQueryOperator.in) as string[];
  const statusSearch = status?.map((status: string) => ({ value: status, label: capitalize(status) })) || [];
  const resolutionStatus = getQueryStringValue(urlQuery.q, 'approval_check.status', ApiQueryOperator.in) as string[];
  const resolutionStatusSearch = resolutionStatus?.map((status: string) => ({ value: status, label: capitalize(status) })) || [];
  const qWithoutDates = (urlQuery.q || []).filter((q: IApiQueryParam) => q.key !== 'payment_date');

  const loadPayoutsDetails = useCallback(() => {
    dispatch(getSitePayoutsRequest(currentSite.id, urlQuery));
    dispatch(getSitePayoutStatsRequest(currentSite.id, startDate, endDate, { q: urlQuery.q }));
  }, [currentSite, startDate, endDate, urlQuery]);

  useEffect(() => {
    loadPayoutsDetails();
  }, [dispatch, currentSite, urlQuery]);

  const exportCSV = async (siteId: string, q?: IApiQueryParam[], sort?: string): Promise<IExportCSV> => {
    const response = await API.DataExport.sitePayouts(siteId, { q: q.filter((item) => item.value !== undefined && item.value !== ''), sort, offset: 0, limit: 0 });
    return CSVFormatter.payouts(response.body);
  };

  const [showModal, hideModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hideModal} isActive>
        <PayoutDetails payout={currentModalPayout} onClose={hideModal} onUpdate={loadPayoutsDetails} />
      </ModalFactory>
    ),
    [currentModalPayout, loadPayoutsDetails]
  );

  return (
    <>
      <DetailsContentHeader isLoading={isLoading} title="Payments" value={`(${totalPayouts})`} zIndex={8}>
        <BusinessDaySection isLoading={isLoading} endDate={endDate} startDate={startDate} setDates={(dates) => onDateRangeChange(formatDateForApi(dates[0]), formatDateForApi(dates[1]))} />
        <FilterButton filterActive={filterVisible} appliedFilters={qWithoutDates.length} onClick={() => setFilterVisible(!filterVisible)} />
        <ButtonExportCSVAsync disabled={!payouts || payouts.length === 0} exportFunc={() => exportCSV(currentSite.id, urlQuery.q, urlQuery.sort)} fileName={`Payments_${currentSite.name}_${startDate}_${endDate}`} />
      </DetailsContentHeader>
      <FilterContainer>
        <FilterWrapper>
          <Filter isVisible={filterVisible}>
            <FieldWrapper label="Name">
              <Input placeholder="Filter by payee name" defaultValue={payeeNameTextSearch} onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnFilterChange(e.currentTarget.value, 'employee_name')} data-cy="name-input" />
            </FieldWrapper>
            <FieldWrapper label="Payment ID">
              <Input placeholder="Enter a payment ID" defaultValue={paymentIdTextSearch} onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnFilterChange(e.currentTarget.value, 'id')} data-cy="payout-id-input" />
            </FieldWrapper>
            <FieldWrapper label="Status">
              <Select
                options={[
                  { label: capitalize(PayoutStatus.PENDING), value: PayoutStatus.PENDING },
                  { label: capitalize(PayoutStatus.EXPIRED), value: PayoutStatus.EXPIRED },
                  { label: capitalize(PayoutStatus.PAID), value: PayoutStatus.PAID },
                  { label: capitalize(PayoutStatus.PAYROLL), value: PayoutStatus.PAYROLL },
                  { label: capitalize(PayoutStatus.CANCELED), value: PayoutStatus.CANCELED },
                ]}
                values={statusSearch}
                multi
                onChange={(selected) => debouncedOnFilterChange(selected, 'status')}
                data-cy="status-select"
              />
            </FieldWrapper>
            <FieldWrapper label="Resolution">
              <Select
                options={[
                  { label: 'Approved', value: PayoutApprovalStatus.REVIEWER_APPROVED },
                  { label: 'Rejected', value: PayoutApprovalStatus.REVIEWER_REJECTED },
                  { label: 'Review Required', value: PayoutApprovalStatus.REVIEW_REQUIRED },
                ]}
                values={resolutionStatusSearch}
                multi
                onChange={(selected) => debouncedOnFilterChange(selected, 'approval_check.status')}
                data-cy="resolution-select"
              />
            </FieldWrapper>
          </Filter>
        </FilterWrapper>
      </FilterContainer>
      <Section data-cy="table">
        <PaymentStatusPills isLoading={isStatsLoading} payoutStats={payoutStats} />
        <PaymentOverviewStats isLoading={isStatsLoading} payoutStats={payoutStats} />
        <PaginatedList total={totalPayouts} limit={urlQuery?.limit} offset={urlQuery?.offset} onPageChange={onQueryChange} isLoading={isLoading || !totalPayouts} standalone={false}>
          <Table scrollable sticky>
            <TableHead>
              <HeaderRow sticky>
                <HeaderCell data-cy="header-payee" onClick={() => onSortChange('employee_name')}>
                  <SortLabel label="Payee" isSorted={isSortedBy('employee_name')} isSortedDesc={isSortDesc} isLoading={isLoading} />
                </HeaderCell>
                <HeaderCell onClick={() => onSortChange('status')} data-cy="header-status">
                  <SortLabel label="Status" isSorted={isSortedBy('status')} isSortedDesc={isSortDesc} isLoading={isLoading} />
                </HeaderCell>
                <HeaderCell onClick={() => onSortChange('payment_date')} data-cy="header-business-day">
                  <SortLabel label="Business Day" isSorted={isSortedBy('payment_date')} isSortedDesc={isSortDesc} isLoading={isLoading} />
                </HeaderCell>
                <HeaderCell align="left">{isLoading ? '' : 'Tips'}</HeaderCell>
                <HeaderCell align="left" onClick={() => onSortChange('autoGratuity')} data-cy="header-autograts">
                  <SortLabel label="Autograts" isSorted={isSortedBy('autoGratuity')} isSortedDesc={isSortDesc} isLoading={isLoading} />
                </HeaderCell>
                <HeaderCell align="left" onClick={() => onSortChange('gross')} data-cy="header-gross">
                  <SortLabel label="Gross" isSorted={isSortedBy('gross')} isSortedDesc={isSortDesc} isLoading={isLoading} />
                </HeaderCell>
                <HeaderCell align="left" onClick={() => onSortChange('holdback')} data-cy="header-holdback">
                  <SortLabel label="Holdback" isSorted={isSortedBy('holdback')} isSortedDesc={isSortDesc} isLoading={isLoading} />
                </HeaderCell>
                <HeaderCell align="left" onClick={() => onSortChange('net')} data-cy="header-net">
                  <SortLabel label="Net" isSorted={isSortedBy('net')} isSortedDesc={isSortDesc} isLoading={isLoading} />
                </HeaderCell>
              </HeaderRow>
            </TableHead>
            <TableBody>
              {!isLoading && totalPayouts === 0 && (
                <EmptyTable>
                  <Empty icon="Payment" message="No payments found." />
                </EmptyTable>
              )}
              {isLoading
                ? [...Array(5)].map((e, i) => <TransactionRow key={i} isLoading />)
                : payouts &&
                  payouts.map((payout: IPayout, index: number) => (
                    <TransactionRow
                      key={index}
                      payout={payout}
                      isLoading={isLoading}
                      onClick={() => {
                        setCurrentModalPayout(payout);
                        showModal();
                      }}
                      data-cy="payout-row"
                    />
                  ))}
            </TableBody>
          </Table>
        </PaginatedList>
      </Section>
    </>
  );
};
